import { API_ENDPOINTS } from '@/config/api.config';
import apiClient from './api';

export interface UserV2Group {
  group_key: string;
  group_name: string;
  roles: UserV2Role[];
}

export interface UserV2Role {
  role_key: string;
  role_name: string;
}

export interface UserV2SocialAccount {
  provider_id: string;
  connected_date: string;
  connected: boolean;
}

export interface UserV2 {
  _id?: string;
  id?: string;
  email: string;
  given_name: string;
  family_name: string;
  password:string;
  full_name?: string;
  personal_email?: string;
  employee_id?: string;
  mobile_number: string;
  profile_image?: string;
  groups?: UserV2Group[];
  meta_data?: Record<string, any>;
  linked_social_accounts?: UserV2SocialAccount[];
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
  email_verified?: boolean;
  mobile_verified?: boolean;
  created_at?: string;
  updated_at?: string;
  last_login?: string;
  last_active_time?: string;
  groups_count?: number;
  courses?: {
    assigned: number;
    completed: number;
  };
  // Organizational Hierarchy
  reporting_manager_email?: string;
  bu_head_email?: string;
  hrbp_email?: string;
}

export interface UserV2CreateRequest {
  email: string;
  given_name: string;
  family_name: string;
  password:string;
  personal_email?: string;
  employee_id?: string;
  mobile_number: string;
  profile_image?: string;
  groups: UserV2Group[];
  meta_data?: Record<string, any>;
  linked_social_accounts?: UserV2SocialAccount[];
  // Organizational Hierarchy
  reporting_manager_email?: string;
  bu_head_email?: string;
  hrbp_email?: string;
}

export interface UserV2UpdateRequest {
  email?: string;
  given_name?: string;
  family_name?: string;
  personal_email?: string;
  employee_id?: string;
  mobile_number?: string;
  profile_image?: string;
  groups?: UserV2Group[];
  meta_data?: Record<string, any>;
  linked_social_accounts?: UserV2SocialAccount[];
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
  // Organizational Hierarchy
  reporting_manager_email?: string;
  bu_head_email?: string;
  hrbp_email?: string;
}

export interface UserV2ListResponse {
  success: boolean;
  code: number;
  description: string;
  data: {
    data: UserV2[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface UserV2Response {
  success: boolean;
  code: number;
  description: string;
  data: UserV2;
}

export interface UserV2StatusUpdateRequest {
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
}

export interface UserV2BulkStatusRequest {
  user_ids: string[];
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
}

export interface UserV2BulkDeleteRequest {
  user_ids: string[];
}

export interface UserV2BulkResponse {
  success: boolean;
  code: number;
  description: string;
  data: {
    success_count: number;
    failure_count: number;
    success_ids: string[];
    failed_ids: string[];
    message: string;
  };
}

export interface UserV2ListFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
  group_key?: string;
  role_key?: string;
  email_verified?: boolean;
  mobile_verified?: boolean;
}

export interface UserV2Metrics {
  total_users: number;
  active_users: number;
  inactive_users: number;
  pending_users: number;
  suspended_users: number;
  pending_invites?: number;
  scim_synced_users?: number;
  recent_registrations?: number;
  pending_verification_users?:number;


}

export interface UserV2MetricsResponse {
  success: boolean;
  code: number;
  description: string;
  data: UserV2Metrics;
}

export interface BulkImportInitiateRequest {
  csv_url: string;
  original_filename: string;
  duplicate_handling?: 'SKIP' | 'UPDATE' | 'ERROR';
  auto_verify_email?: boolean;
  auto_verify_mobile?: boolean;
  default_status?: 'ACTIVE' | 'INACTIVE' | 'PENDING_VERIFICATION';
}

export interface BulkImportResult {
  import_id: string;
  status: 'PENDING' | 'INITIATED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  message: string;
}

export interface BulkImportInitiateResponse {
  success: boolean;
  code: number;
  description: string;
  data: BulkImportResult;
}

export interface BulkImportMetrics {
  total_records: number;
  processed_records: number;
  success_count: number;
  failed_count: number;
  skipped_count: number;
  progress_percentage: number;
}
 
export interface BulkImportStatus {
  import_id: string;
  status: 'PENDING' | 'INITIATED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  metrics: BulkImportMetrics;
  total_records: number;
  processed_records: number;
  successful_records: number;
  failed_records: number;
  skipped_records: number;
  start_time: string;
  end_time?: string;
  error_message?: string;
  validation_errors?: string[];
  created_at: string;
  updated_at: string;
}

export interface BulkImportStatusResponse {
  success: boolean;
  code: number;
  description: string;
  data: BulkImportStatus;
}

export interface BulkImportHistory {
  import_id: string;
  original_filename: string;
  file_size_bytes: number;
  status: string;
  initiated_at: string;
  completed_at: string;
  initiated_by: string;
  metrics: {
    total_records: number;
    processed_records: number;
    success_count: number;
    failed_count: number;
    skipped_count: number;
    progress_percentage: number;
  };
}

export interface BulkImportHistoryResponse {
  success: boolean;
  code: number;
  data: {
    imports: BulkImportHistory[];
    total: number;
    page: number;
    limit: number;
  };
  meta_data?: string;
}

export interface BulkImportRecord {
  record_data: any;
  record_id: string;
  row_number: number;
  status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'SKIPPED';
  user_id?: string;
  email: string;
  given_name: string;
  family_name: string;
  mobile_number?: string;
  employee_id?: string;
  groups?: string;
  error_message?: string;
  error_details?: Record<string, any>;
  processed_at?: string;
}

export interface BulkImportRecordsResponse {
  success: boolean;
  code: number;
  description: string;
  data: {
    records: BulkImportRecord[];
    total: number;
    page: number;
    limit: number;
    import_id: string;
    filters: {
      status?: string;
      search?: string;
    };
  };
}

export interface FileUploadResponse {
  success: boolean;
  code: number;
  description?: string;
  data: {
    name: string;
    key: string;
    url: string;
    fileType: string;
  };
}

class UserManagementV2Service {

  async getUsersList(filters: UserV2ListFilters = {}): Promise<UserV2ListResponse> {
    try {
      const payload: Record<string, any> = {};

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          payload[key] = value;
        }
      });

      const response = await apiClient.post<UserV2ListResponse>(
        API_ENDPOINTS.USERS.LIST,
        payload
      );
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to fetch users');
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async getUserById(userId: string): Promise<UserV2Response> {
    try {
      const response = await apiClient.get<UserV2Response>(API_ENDPOINTS.USERS.GET_BY_ID(userId));
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to fetch user');
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  async createUser(userData: UserV2CreateRequest): Promise<UserV2Response> {
    try {
      const response = await apiClient.post<UserV2Response>(API_ENDPOINTS.USERS.CREATE, userData);
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to create user');
    } catch (error: any) {
      console.error('Error creating user:', error);
      
      // Handle 409 Conflict errors specifically
      if (error?.response?.status === 409) {
        const apiError = error.response.data?.error;
        if (apiError) {
          // Use error_description from API as it contains the original (unencrypted) email
          if (apiError.error_code === 'mobile_number_already_exists') {
            throw new Error(apiError.error_description || `Mobile number is already registered. Please use a different mobile number.`);
          }
          if (apiError.error_code === 'email_already_exists') {
            throw new Error(apiError.error_description || `Email is already registered. Please use a different email address.`);
          }
          // Generic conflict message
          throw new Error(apiError.error_description || 'This user already exists. Please check the email and mobile number.');
        }
      }
      
      // Handle API error response with error object
      if (error?.response?.data?.error) {
        const apiError = error.response.data.error;
        throw new Error(apiError.error_description || apiError.error_code || 'Failed to create user');
      }
      
      // Handle validation errors
      if (error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors
          .map((err: any) => `${err.field}: ${err.message}`)
          .join(', ');
        throw new Error(validationErrors);
      }
      
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to create user'
      );
    }
  }

  async updateUser(userId: string, userData: UserV2UpdateRequest): Promise<UserV2Response> {
    try {
      const response = await apiClient.put<UserV2Response>(API_ENDPOINTS.USERS.UPDATE(userId), userData);
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to update user');
    } catch (error: any) {
      console.error('Error updating user:', error);
      
      // Handle 409 Conflict errors specifically
      if (error?.response?.status === 409) {
        const apiError = error.response.data?.error;
        if (apiError) {
          // Use error_description from API as it contains the original (unencrypted) email
          if (apiError.error_code === 'mobile_number_already_exists') {
            throw new Error(apiError.error_description || `Mobile number is already registered by another user. Please use a different mobile number.`);
          }
          if (apiError.error_code === 'email_already_exists') {
            throw new Error(apiError.error_description || `Email is already registered by another user. Please use a different email address.`);
          }
          // Generic conflict message
          throw new Error(apiError.error_description || 'This information is already in use by another user.');
        }
      }
      
      // Handle API error response with error object
      if (error?.response?.data?.error) {
        const apiError = error.response.data.error;
        throw new Error(apiError.error_description || apiError.error_code || 'Failed to update user');
      }
      
      // Handle validation errors
      if (error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors
          .map((err: any) => `${err.field}: ${err.message}`)
          .join(', ');
        throw new Error(validationErrors);
      }
      
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to update user'
      );
    }
  }

  async restoreUser(userId: string): Promise<UserV2Response> {
    try {
      const response = await apiClient.post<UserV2Response>(`${API_ENDPOINTS.USERS.UPDATE(userId)}/restore`);

      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to restore user');
    } catch (error: any) {
      console.error('Error restoring user:', error);

      // Handle specific errors
      if (error?.response?.status === 403) {
        throw new Error('Cannot restore user - 30-day recovery period has expired');
      }

      if (error?.response?.data?.error) {
        const apiError = error.response.data.error;
        throw new Error(apiError.error_description || 'Failed to restore user');
      }

      throw new Error(error?.message || 'Failed to restore user');
    }
  }

  async unlockUser(userId: string): Promise<UserV2Response> {
    try {
      const response = await apiClient.post<UserV2Response>(`${API_ENDPOINTS.USERS.UPDATE(userId)}/unlock`);

      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to unlock user');
    } catch (error: any) {
      console.error('Error unlocking user:', error);

      if (error?.response?.status === 400) {
        throw new Error('User account is not locked');
      }

      if (error?.response?.data?.error) {
        const apiError = error.response.data.error;
        throw new Error(apiError.error_description || 'Failed to unlock user');
      }

      throw new Error(error?.message || 'Failed to unlock user');
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.USERS.DELETE(userId));
      
      if (!response.data.success && response.status !== 204) {
        throw new Error(response.data?.description || 'Failed to delete user');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      
      // Handle API error response with error object
      if (error?.response?.data?.error) {
        const apiError = error.response.data.error;
        throw new Error(apiError.error_description || apiError.error_code || 'Failed to delete user');
      }
      
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to delete user'
      );
    }
  }

  async updateUserStatus(userId: string, status: string): Promise<UserV2Response> {
    try {
      const response = await apiClient.patch<UserV2Response>(
        API_ENDPOINTS.USERS.UPDATE_STATUS(userId), 
        { status }
      );
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to update user status');
    } catch (error: any) {
      console.error('Error updating user status:', error);
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to update user status'
      );
    }
  }

  async bulkUpdateStatus(data: UserV2BulkStatusRequest): Promise<UserV2BulkResponse> {
    try {
      const response = await apiClient.patch<UserV2BulkResponse>(API_ENDPOINTS.USERS.BULK_STATUS_UPDATE, data);
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to bulk update status');
    } catch (error: any) {
      console.error('Error bulk updating status:', error);
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to bulk update status'
      );
    }
  }

  async bulkDeleteUsers(data: UserV2BulkDeleteRequest): Promise<UserV2BulkResponse> {
    try {
      const response = await apiClient.delete<UserV2BulkResponse>(API_ENDPOINTS.USERS.BULK_DELETE, { data });
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to bulk delete users');
    } catch (error: any) {
      console.error('Error bulk deleting users:', error);
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to bulk delete users'
      );
    }
  }

  async getUserMetrics(): Promise<UserV2MetricsResponse> {
    try {
      const response = await apiClient.get<UserV2MetricsResponse>(API_ENDPOINTS.USERS.METRICS);
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to fetch user metrics');
    } catch (error: any) {
      console.error('Error fetching user metrics:', error);
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to fetch user metrics'
      );
    }
  }

  async uploadFile(file: File): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.put<FileUploadResponse>(API_ENDPOINTS.STORAGE.UPLOAD, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to upload file');
    } catch (error: any) {
      console.error('Error uploading file:', error);
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to upload file'
      );
    }
  }

  async initiateBulkImport(data: BulkImportInitiateRequest): Promise<BulkImportInitiateResponse> {
    try {
      const response = await apiClient.post<BulkImportInitiateResponse>(API_ENDPOINTS.USER_BULK_IMPORT.INITIATE, data);
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to initiate bulk import');
    } catch (error: any) {
      console.error('Error initiating bulk import:', error);
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to initiate bulk import'
      );
    }
  }

  async getBulkImportStatus(importId: string): Promise<BulkImportStatusResponse> {
    try {
      const response = await apiClient.get<BulkImportStatusResponse>(`/api/users-bulk-import/${importId}/status`);
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to get bulk import status');
    } catch (error: any) {
      console.error('Error getting bulk import status:', error);
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to get bulk import status'
      );
    }
  }

  async getBulkImportHistory(params?: { page?: number; limit?: number }): Promise<BulkImportHistoryResponse> {
    try {
      const response = await apiClient.get<BulkImportHistoryResponse>(API_ENDPOINTS.USER_BULK_IMPORT.LIST, { params });
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to get bulk import history');
    } catch (error: any) {
      console.error('Error getting bulk import history:', error);
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to get bulk import history'
      );
    }
  }

  async resetUserPassword(userId: string, newPassword: string, requestId?: string): Promise<any> {
    try {
      // Using the correct V2 admin password reset endpoint
      const response = await apiClient.patch(API_ENDPOINTS.USERS.ADMIN_PASSWORD_RESET(userId), {
        password: newPassword,
        ...(requestId && { request_id: requestId })
      });
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.message || 'Failed to reset password');
    } catch (error: any) {
      console.error('Error resetting password:', error);
      
      // Handle specific error cases
      if (error?.response?.status === 400) {
        throw new Error('Invalid password. Password must be at least 8 characters long.');
      }
      
      if (error?.response?.status === 404) {
        throw new Error('User not found');
      }
      
      throw new Error(
        error?.response?.data?.message || 
        error?.response?.data?.description || 
        error?.message || 
        'Failed to reset password'
      );
    }
  }

  async getBulkImportRecords(
    importId: string, 
    params?: { 
      page?: number; 
      limit?: number; 
      status?: 'PENDING' | 'SUCCESS' | 'FAILED' | 'SKIPPED';
      search?: string;
    }
  ): Promise<BulkImportRecordsResponse> {
    try {
      const response = await apiClient.get<BulkImportRecordsResponse>(
        API_ENDPOINTS.USER_BULK_IMPORT.RECORDS(importId), 
        { params }
      );
      
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.description || 'Failed to get bulk import records');
    } catch (error: any) {
      console.error('Error getting bulk import records:', error);
      throw new Error(
        error?.response?.data?.description || 
        error?.message || 
        'Failed to get bulk import records'
      );
    }
  }

  /**
   * Export users to CSV with optional filters
   * @param filters - Optional filters to apply (search, status, group_key, role_key, etc.)
   * @returns Promise with CSV blob
   */
  async exportUsers(filters: Omit<UserV2ListFilters, 'page' | 'limit'> = {}): Promise<Blob> {
    try {
      const params = new URLSearchParams();

      // Add filters to query parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await apiClient.get(
        `${API_ENDPOINTS.USERS.EXPORT}${params.toString() ? `?${params.toString()}` : ''}`,
        {
          responseType: 'blob', // Important: Tell axios to expect binary data
        }
      );

      // The response should already be a Blob
      return response.data;
    } catch (error: any) {
      console.error('Error exporting users:', error);
      throw new Error(
        error?.response?.data?.description ||
        error?.message ||
        'Failed to export users'
      );
    }
  }
}

export const userManagementV2Service = new UserManagementV2Service();